import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";

const TITLE = "Recursos do TrajetoFácil — o que cada função faz";
const DESCRIPTION =
  "Combustível, pedágio por eixos, ida e volta, múltiplas paradas, custo por km, multiplicador de repasse e link compartilhável: entenda cada recurso da calculadora.";
const URL = "https://www.trajetofacil.com.br/recursos";

const FEATURES = [
  {
    id: "combustivel",
    title: "Cálculo de combustível",
    body: "Você informa o preço do litro e o consumo em km/l; o sistema divide a distância de cada trecho pelo consumo e multiplica pelo preço. O gasto aparece separado por sentido, nunca misturado ao pedágio.",
    tip: "Meça o consumo real abastecendo o tanque cheio duas vezes: o valor de catálogo costuma ser otimista.",
  },
  {
    id: "pedagio",
    title: "Estimativa de pedágio",
    body: "A rota traçada é cruzada com a base de praças georreferenciadas. Cada praça no caminho entra com a tarifa da categoria do veículo, e o resultado mostra o detalhe praça por praça, não só o total.",
    tip: "Se uma praça conhecida não aparecer, reporte pelo contato: conferimos na fonte oficial e corrigimos.",
  },
  {
    id: "eixos",
    title: "Categoria e número de eixos",
    body: "Carro, moto, ônibus e caminhão pagam tarifas diferentes na mesma praça. Para veículos comerciais, o número de eixos multiplica a tarifa base — informar o número exato é o que torna a estimativa de frete confiável.",
    tip: "Caminhão vazio com eixo suspenso pode pagar por menos eixos em várias concessões.",
  },
  {
    id: "ida-e-volta",
    title: "Ida e volta",
    body: "Com a opção ativada, os dois sentidos são calculados como rotas independentes. Isso revela quando a volta custa menos — caso de praças que cobram em um único sentido — em vez de simplesmente dobrar a ida.",
    tip: "Compare os dois cards: diferenças grandes costumam indicar praça de sentido único no trajeto.",
  },
  {
    id: "paradas",
    title: "Múltiplos pontos (até seis)",
    body: "Cada parada cria um trecho próprio, com distância, combustível e praças específicas. A ordem em que você adiciona é a ordem do roteiro, e o custo total soma todos os trechos.",
    tip: "Teste duas ordens diferentes e compare: o mesmo conjunto de paradas pode variar dezenas de quilômetros.",
  },
  {
    id: "custo-por-km",
    title: "Custo por quilômetro",
    body: "O total da viagem dividido pela distância percorrida. É o indicador que permite comparar veículos, rotas e propostas com a mesma régua, e a base de qualquer cobrança por deslocamento.",
    tip: "Deixando preço do litro e km/l em branco, você pode trabalhar direto com um valor fechado por km.",
  },
  {
    id: "multiplicador",
    title: "Multiplicador de repasse",
    body: "Aplica um fator sobre o custo calculado: 1,30 significa 30% de margem. O valor a repassar aparece ao lado do custo puro, útil para cobrar de um cliente, ratear entre passageiros ou precificar um frete.",
    tip: "O multiplicador cobre tempo, desgaste e custos fixos — não é lucro líquido.",
  },
  {
    id: "compartilhar",
    title: "Compartilhar a simulação por link",
    body: "O link gerado carrega origem, destino, paradas, veículo e parâmetros de custo. Quem abrir vê exatamente a mesma simulação, sem precisar digitar nada — prático para anexar a um orçamento ou combinar uma viagem.",
    tip: "Guarde o link junto da proposta: ele funciona como memória de cálculo do valor apresentado.",
  },
];

export const Route = createFileRoute("/recursos")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: FeaturesPage,
});

function FeaturesPage() {
  return (
    <SiteLayout>
      <article className="space-y-10 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Recursos do TrajetoFácil</h1>
          <p className="mt-3 text-muted-foreground">
            Cada função da calculadora existe para responder uma pergunta concreta sobre o custo da
            viagem. Veja o que cada uma faz, como usar e o cuidado que vale ter.
          </p>
        </header>

        <div className="space-y-5">
          {FEATURES.map((f) => (
            <section key={f.id} id={f.id} className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-lg font-bold">{f.title}</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{f.body}</p>
              <p className="mt-3 text-sm leading-6 text-foreground">
                <strong className="font-semibold">Dica: </strong>
                {f.tip}
              </p>
            </section>
          ))}
        </div>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Para se aprofundar</h2>
          <p>
            As fórmulas por trás de cada recurso estão na{" "}
            <Link to="/metodologia" className="font-medium text-primary underline underline-offset-4">
              metodologia
            </Link>
            ; o passo a passo do uso está em{" "}
            <Link to="/como-funciona" className="font-medium text-primary underline underline-offset-4">
              como funciona
            </Link>
            ; e os{" "}
            <Link to="/guias" className="font-medium text-primary underline underline-offset-4">
              guias
            </Link>{" "}
            trazem exemplos numéricos completos de cada tipo de cálculo.
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
